/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { Button, Title } from '@components/v2/ui';
import { Box, Flex } from '@mantine/core';
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import { STEPS } from '../DrawerStepperOffset/DrawerStepperOffset.config';
import { Stepper } from '../Stepper/Stepper';
import classes from './StepperHeader.module.css';

/* -------------------------------------------------------------------------- */
/*                                  Types                                     */
/* -------------------------------------------------------------------------- */

/**
 * Props for the StepperHeader component
 */
interface StepperHeaderProps {
	/** Callback when cancel button is clicked */
	onCancel: () => void;
	/** Callback when previous button is clicked */
	onPrevious: () => void;
	/** Callback when next button is clicked */
	onNext: () => void;
	/** Current active step index */
	activeStep: number;
}

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

/**
 * Header for the offset creation stepper, displaying navigation buttons
 * and stepper progress indicator.
 */
const StepperHeader = ({
	onCancel,
	onPrevious,
	onNext,
	activeStep,
}: StepperHeaderProps) => {
	const { t } = useTranslation();

	/* -------------------------- Store data -------------------------- */
	const {
		offsetMesurabilityProjectsSelected,
		offsetAdditionalProjectsSelected,
		offsetCreationDatas,
		offsetProducedAddis,
	} = useStore();

	/* -------------------------- Step flags -------------------------- */
	const isStep1 = activeStep === 0;
	const isStep2 = activeStep === 1;
	const isStep3 = activeStep === 2;
	const isStep4 = activeStep === 3;

	/* -------------------------- Calculations -------------------------- */
	const toNumber = (value: unknown): number =>
		typeof value === 'number' && !Number.isNaN(value)
			? value
			: 0;

	const totalMesurabilityQuantity: number =
		offsetMesurabilityProjectsSelected.reduce(
			(sum, { quantitytco2eq }) =>
				sum + toNumber(quantitytco2eq),
			0
		);

	const totalAdditionalityPrice: number =
		offsetAdditionalProjectsSelected.reduce(
			(sum, { price }) => sum + toNumber(price),
			0
		);

	/* -------------------------- Validation -------------------------- */
	const isStep2Invalid =
		isStep2 &&
		totalMesurabilityQuantity !==
			(offsetCreationDatas?.quantity ?? 0);
	const isStep3Invalid =
		isStep3 &&
		totalAdditionalityPrice !==
			(offsetProducedAddis ?? 0);

	const isNextDisabled =
		isStep2Invalid || isStep3Invalid || isStep4;

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes['container-header']}>
			{/* Top controls: Cancel / Title / Previous & Next */}
			<Flex
				wrap="wrap"
				justify="space-between"
				align="center"
				gap="xs"
			>
				<Flex gap="12px" align="center">
					<Button
						w={160}
						variant="outline"
						type="button"
						onClick={onCancel}
					>
						{t('global.cancel')}
					</Button>
					<Title
						ta="center"
						c="var(--mantine-color-igluPrimary-9)"
						order={6}
					>
						{t('offsets.addOffset')}
					</Title>
				</Flex>

				<Flex gap="12px" align="center">
					<Button
						w={160}
						variant="outline"
						type="button"
						onClick={onPrevious}
						disabled={isStep1}
					>
						{t('global.previous')}
					</Button>
					<Button
						w={160}
						variant="primary"
						type="button"
						onClick={onNext}
						disabled={isNextDisabled || isStep4}
					>
						{t('global.next')}
					</Button>
				</Flex>
			</Flex>

			{/* Stepper progress */}
			<Box mx={20}>
				<Stepper
					readOnly
					options={STEPS}
					active={activeStep}
					iconSize={32}
				/>
			</Box>
		</Box>
	);
};

export default StepperHeader;
